/**
 * THE OPERATION REGISTRY — the only door from this app to the server, and the thing that makes
 * reachability a COMPILED FACT rather than a claim.
 *
 * ── WHAT THIS REPLACES, AND WHY ─────────────────────────────────────────────────────────────────
 *
 * `reachability.ts` used to map a route id to a SCREEN NAME, and its test proved that a `.tsx` file
 * with that basename existed. Two independent reviews found the same defect: the invariant the map
 * asserts — *"a person can perform this action"* — was never the property being checked. A screen
 * that renders a form and posts nothing satisfied it. `MfaChallengeScreen.tsx` exists, and its
 * submit handler sets a local boolean; `auth.mfa.verify` was certified reachable for months.
 *
 * The file-existence check could not have found that, and neither could a grep for URLs: the URLs
 * were built from template literals in fifteen different shapes. So the fix is not a better scanner.
 * It is to REMOVE the ability to write a URL in a screen at all.
 *
 * ── THE MECHANISM ───────────────────────────────────────────────────────────────────────────────
 *
 *   1. Every declared route is an entry here, keyed by `routeId`, carrying the method and the path
 *      template. `reachability.test.ts` asserts this table equals `registry/routes.baseline.json`
 *      — the approved artifact `write-registry.ts` regenerates and `route-baseline-diff` compares —
 *      in both directions, field for field. A route that changes its URL and does not change this
 *      table fails; a table entry naming a route the server does not serve fails.
 *
 *   2. A caller names an OPERATION, never a path: `callOperation('tenant.role.create', …)`. The id
 *      is typed as `OperationId`, so a misspelling is a compile error rather than a 404.
 *
 *   3. The URL IS BUILT HERE from the template and typed parameters. `ParamsOf<Id>` is extracted
 *      from the path template by the type system, so a call to an operation whose path contains
 *      `{tenant_uid}` does not compile without a `tenant_uid`, and a call that supplies a parameter
 *      the template has no slot for does not compile either.
 *
 *   4. The REQUEST SHAPE is `OperationBodies[Id]`. A write whose body is not declared here cannot be
 *      called: the type resolves to `UndeclaredBody`, which nothing satisfies. G-01's third defect
 *      was a body of `{email, password}` sent to a route reading `{login, password}` — a wrong field
 *      name at the right URL, answered by the endpoint's generic 401 and therefore indistinguishable
 *      from a wrong password. `reachability.test.ts` reconciles every field declared here against the
 *      field names the owning handler in `apps/api/src/routes/` actually reads.
 *
 *   5. Because every call site names a literal operation id, "which routes does this app reach" is
 *      answerable by reading the source — and `web-client-choke-point` (guardrail G-03) proves the
 *      two preconditions that make that reading COMPLETE: no module outside this file reaches the
 *      transport, and no `callOperation` is passed a computed id the scanner cannot resolve.
 *
 * ── WHAT THIS DOES NOT PROVE ────────────────────────────────────────────────────────────────────
 *
 * It proves the wire from a control in a screen to a declared route, with the method, the URL and
 * the field names the handler reads. It does NOT prove the gate allows the caller, that the service
 * behind the route does the right thing, or that the repository writes what the service intended —
 * those are `apps/api`'s own tests, and this file cannot see them. What it removes is the class of
 * defect where the browser half and the server half are each correct and do not meet.
 */

import { API_BASE, NetworkError, interpretResponse, requestInit } from './client';
import type {
  ArrearsReport,
  ChangeRequestRead,
  DunningPolicyReport,
  DunningReviewRead,
  AuditEventSummary,
  BenefitView,
  CompanyGrantSummary,
  OffboardingStateView,
  PurgeEligibility,
  CompanySummary,
  MembershipSummary,
  PlatformAuditEventSummary,
  BillingStatement,
  NextChargeEstimate,
  PlanOffersView,
  CapacityUsage,
  RunReport,
  ChargeDimensionEntry,
  ModuleCatalogueEntry,
  PlanCatalogueEntry,
  PlatformHealthSummary,
  ReleaseAvailability,
  ReleaseOverview,
  SkuCatalogueEntry,
  CommercialPolicySummary,
  PlatformMetricsSummary,
  PriceBookSummary,
  PriceItemSummary,
  PlatformSubscriptionSummary,
  SubscriptionSummary,
  SupportGrantSummary,
  TenantAdminSummary,
  RoleSummary,
  ConnectorHealthSummary,
  ExternalCompanySummary,
  SessionSummary,
  SourceConnectionSummary,
  UserSessionSummary,
  TenantSummary,
  TenantSupportGrantView,
  MarketCardView,
  PreferenceKey,
  WorkItemState,
  WorkItemSummary,
  ReminderRuleView,
  CompanyReminderSettingView,
  PublicPlanOfferView,
  SignupQuoteView,
  WorkItemDetailView,
  StoredCardView,
  ChargeAttemptView,
  ModuleOffersView,
  ClearingOverviewView,
} from './contract';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export interface OperationSpec {
  readonly method: HttpMethod;
  /** The DECLARED path, `/api`-prefixed, with `{param}` placeholders exactly as the baseline holds them. */
  readonly path: string;
}

/**
 * Every route in `registry/routes.baseline.json`, including the four MACHINE ones and every human
 * action no screen reaches yet.
 *
 * The whole set is here on purpose. A table containing only the operations somebody wired would make
 * "unreached" unrepresentable — the gap would be an absence, and an absence is what nobody counts.
 * Here an unreached route is an entry with no call site, which is a fact the scanner can state.
 */
export const OPERATIONS = {
  'auth.invitation.accept': { method: 'POST', path: '/api/auth/invitation' },
  'auth.mfa.enrol': { method: 'POST', path: '/api/auth/mfa/enrolment' },
  'auth.mfa.enrolment.confirm': { method: 'POST', path: '/api/auth/mfa/enrolment/confirm' },
  'auth.mfa.verify': { method: 'POST', path: '/api/auth/mfa' },
  /* Satisfy a step-up on the CURRENT session. Section 3, 2026-10-25. */
  'auth.stepup.assert': { method: 'POST', path: '/api/me/stepup' },
  'auth.password.reset_complete': { method: 'POST', path: '/api/auth/password/reset' },
  'auth.password.reset_request': { method: 'POST', path: '/api/auth/password/reset-request' },
  'auth.session.create': { method: 'POST', path: '/api/auth/session' },
  'auth.session.delete': { method: 'POST', path: '/api/auth/signout' },
  'auth.whoami.read': { method: 'GET', path: '/api/me' },
  'companies.company.create': { method: 'POST', path: '/api/companies' },
  'companies.company.lifecycle': { method: 'POST', path: '/api/companies/{company_uid}/lifecycle' },
  'companies.company.read': { method: 'GET', path: '/api/companies/{company_uid}' },
  'companies.company.update': { method: 'PUT', path: '/api/companies/{company_uid}' },
  'companies.conflict.read': { method: 'GET', path: '/api/companies/conflicts' },
  'companies.identifier.read': { method: 'GET', path: '/api/companies/{company_uid}/identifiers' },
  'companies.list.read': { method: 'GET', path: '/api/companies' },
  /* The office REGISTER: every company the office has, not only the ones I may open. */
  'companies.register.read': { method: 'GET', path: '/api/tenant/companies' },
  'companies.portfolio.assign': { method: 'PUT', path: '/api/companies/{company_uid}/portfolio' },
  'companies.source_binding.create': { method: 'PUT', path: '/api/companies/{company_uid}/source-bindings' },
  'companies.source_system.read': { method: 'GET', path: '/api/companies/{company_uid}/source-system' },
  'companies.source_system.select': { method: 'PUT', path: '/api/companies/{company_uid}/source-system' },
  'connector.enrolment.redeem': { method: 'POST', path: '/api/connector/enrolment' },
  'connector.envelope.accept': { method: 'POST', path: '/api/connector/envelope' },
  'portal.identity.read': { method: 'GET', path: '/api/portal' },
  'platform.audit.read': { method: 'GET', path: '/api/platform/audit' },
  'platform.discount.apply': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription/discounts' },
  'platform.discount.apply_full': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription/discounts/full' },
  'platform.discount.approve': { method: 'POST', path: '/api/platform/discounts/{discount_uid}/approval' },
  'platform.entitlement.explain': { method: 'POST', path: '/api/platform/entitlement/explain' },
  'platform.health.read': { method: 'GET', path: '/api/health' },
  'platform.limit.company.set': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/limits/company' },
  'platform.limit.seat.set': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/limits/seat' },
  'platform.subscription.create': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription' },
  'platform.subscription.plan.assign': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription/plan' },
  'platform.subscription.sku.add': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription/skus' },
  'platform.subscription.plan.apply': {
    method: 'POST',
    path: '/api/platform/tenants/{tenant_uid}/subscription/plan-skus',
  },
  'platform.metrics.read': { method: 'GET', path: '/api/platform/metrics' },
  'platform.price_book.read': { method: 'GET', path: '/api/platform/price-books' },
  'platform.price_book.items.read': { method: 'GET', path: '/api/platform/price-books/{price_book_uid}/items' },
  'platform.price_book.create': { method: 'POST', path: '/api/platform/price-books' },
  'platform.price_book.item.add': { method: 'POST', path: '/api/platform/price-books/{price_book_uid}/items' },
  'platform.price_book.publish': { method: 'POST', path: '/api/platform/price-books/{price_book_uid}/publication' },
  'platform.subscription.price_book.read': {
    method: 'GET',
    path: '/api/platform/tenants/{tenant_uid}/subscription/price-book',
  },
  /* 2026-11-01. The reading half of the benefits interface: the same address as the two writes,
     by the method that distinguishes them. */
  'platform.discount.read': {
    method: 'GET',
    path: '/api/platform/tenants/{tenant_uid}/subscription/discounts',
  },
  'platform.subscription.price_book.set': {
    method: 'POST',
    path: '/api/platform/tenants/{tenant_uid}/subscription/price-book',
  },
  'platform.catalogue.plan.read': { method: 'GET', path: '/api/platform/catalogue/plans' },
  'platform.catalogue.sku.read': { method: 'GET', path: '/api/platform/catalogue/skus' },
  /* X3 (R-2) — the catalogue's write half, and the seeded vocabularies the two forms choose from. */
  'platform.catalogue.reference.read': {
    method: 'GET',
    path: '/api/platform/catalogue/reference',
  },
  'platform.catalogue.plan.create': { method: 'POST', path: '/api/platform/catalogue/plans' },
  'platform.catalogue.sku.create': { method: 'POST', path: '/api/platform/catalogue/skus' },
  'platform.catalogue.sku.module.attach': {
    method: 'POST',
    path: '/api/platform/catalogue/skus/{sku_code}/modules',
  },
  'platform.catalogue.plan.sku.include': {
    method: 'POST',
    path: '/api/platform/catalogue/plans/{plan_code}/skus',
  },
  'platform.catalogue.plan.sku.quantity': {
    method: 'PUT',
    path: '/api/platform/catalogue/plans/{plan_code}/skus/{sku_code}',
  },
  'platform.catalogue.plan.sku.exclude': {
    method: 'DELETE',
    path: '/api/platform/catalogue/plans/{plan_code}/skus/{sku_code}',
  },
  'platform.incidents.read': { method: 'GET', path: '/api/platform/health' },
  'platform.release.available.read': { method: 'GET', path: '/api/platform/release' },
  'platform.release.update.start': { method: 'POST', path: '/api/platform/release/update' },
  'platform.release.accept': { method: 'POST', path: '/api/platform/release/acceptance' },
  'platform.capacity.read': { method: 'GET', path: '/api/platform/tenants/{tenant_uid}/capacity' },
  /* 2026-11-02. Where one customer's money stands, and the console that collects it. */
  'platform.tenant.statement.read': {
    method: 'GET',
    path: '/api/platform/tenants/{tenant_uid}/billing-statement',
  },
  /* 2026-11-03. Item 3 — what the next period would cost, as a projection and labelled one. */
  'platform.tenant.estimate.read': {
    method: 'GET',
    path: '/api/platform/tenants/{tenant_uid}/billing-estimate',
  },
  'platform.billing.due.read': { method: 'GET', path: '/api/platform/billing/due' },
  'platform.billing.run.execute': { method: 'POST', path: '/api/platform/billing/run' },
  /* 2026-11-03. The arrears list and the day counts: the dunning service had no route at all. */
  /*
   * 0049. A CREDIT REDUCES A DEBT. The read exists so the console does not compute the
   * remaining balance itself — that would be a second copy of the service's arithmetic.
   */
  'platform.billing.credit.remaining': {
    method: 'GET',
    path: '/api/platform/tenants/{tenant_uid}/documents/{document_uid}/creditable',
  },
  'platform.billing.credit.issue': {
    method: 'POST',
    path: '/api/platform/tenants/{tenant_uid}/documents/{document_uid}/credits',
  },
  'platform.change_request.read': { method: 'GET', path: '/api/platform/change-requests' },
  'platform.change_request.resolve': {
    method: 'POST',
    path: '/api/platform/change-requests/{change_request_uid}',
  },
  'platform.dunning.read': { method: 'GET', path: '/api/platform/dunning' },
  'platform.dunning.review': { method: 'POST', path: '/api/platform/dunning/review' },
  'platform.dunning.policy.read': { method: 'GET', path: '/api/platform/dunning/policy' },
  'platform.dunning.policy.set': { method: 'POST', path: '/api/platform/dunning/policy' },
  'platform.policy.read': { method: 'GET', path: '/api/platform/commercial-policy' },
  'platform.policy.trial_days.set': { method: 'POST', path: '/api/platform/commercial-policy/trial-days' },
  // §10ב. The public door's two locks. Both carry `platform.catalogue.plan.manage`.
  'platform.signup_door.read': { method: 'GET', path: '/api/platform/signup-door' },
  'platform.signup_door.set': { method: 'POST', path: '/api/platform/signup-door' },
  'platform.catalogue.plan.public_signup.set': {
    method: 'POST',
    path: '/api/platform/catalogue/plans/public-signup',
  },
  'platform.subscription.read': { method: 'GET', path: '/api/platform/tenants/{tenant_uid}/subscription' },
  'platform.subscription.state.change': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription/state' },
  'platform.support_grant.approve': { method: 'POST', path: '/api/platform/support-grants/{grant_uid}/approval' },
  'platform.support_grant.read': { method: 'GET', path: '/api/platform/support-grants' },
  'platform.support_grant.request': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/support-grants' },
  'platform.support_grant.revoke': { method: 'POST', path: '/api/platform/support-grants/{grant_uid}/revocation' },
  'platform.tenant.activate': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/administrator' },
  'platform.tenant.close': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/closure' },
  'platform.tenant.create': { method: 'POST', path: '/api/platform/tenants' },
  'platform.tenant.offboarding.purge_eligibility': { method: 'GET', path: '/api/platform/tenants/{tenant_uid}/offboarding/purge-eligibility' },
  'platform.tenant.offboarding.read': { method: 'GET', path: '/api/platform/tenants/{tenant_uid}/offboarding' },
  'platform.tenant.offboarding.reinstate': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/offboarding/reinstatement' },
  'platform.tenant.offboarding.terminate': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/offboarding/termination' },
  'platform.tenant.reactivate': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/reactivation' },
  'platform.tenant.retention_policy.set': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/retention-policy' },
  'platform.tenant.read': { method: 'GET', path: '/api/platform/tenants' },
  'platform.tenant.suspend': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/suspension' },
  'platform.trial.extend': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription/trial/extension' },
  'platform.trial.set': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription/trial' },
  'tenant.audit.read': { method: 'GET', path: '/api/tenant/audit' },
  'tenant.billing.read': { method: 'GET', path: '/api/tenant/subscription' },
  /*
   * THE OFFICE'S OWN QUOTAS, READ SEPARATELY. 2026-11-02: `SubscriptionSummary.capacity` was
   * declared and produced by nothing, so the panel was handed `undefined`. Usage is a live count,
   * not a column of the subscription row, and a read of its own is what lets the screen tell
   * "no quotas are recorded" from "the quotas could not be read".
   */
  'tenant.capacity.read': { method: 'GET', path: '/api/tenant/capacity' },
  /* The office's own statement: its documents, its next attempt, its standing. */
  'tenant.statement.read': { method: 'GET', path: '/api/tenant/billing-statement' },
  'tenant.billing.estimate.read': { method: 'GET', path: '/api/tenant/billing-estimate' },
  /* ITEM 4. The plans this office may move onto, priced; and the move itself. */
  'tenant.plan.offers.read': { method: 'GET', path: '/api/tenant/plan-offers' },
  'tenant.subscription.plan.change': { method: 'POST', path: '/api/tenant/subscription/plan' },
  /*
   * 0046. THE ACT THE OWNER'S SECOND STAGE IS NAMED FOR — *"לאחר 14 יום, הסדרה בלבד"*.
   * It takes NO BODY: the subscription is read from the caller's own bound context, because
   * `only` is a filter the run honours and a uid in a body field would charge the neighbour.
   */
  /* 0047. The path for what an office cannot self-serve — 08 §12B.3 row 17. */
  'tenant.change_request.submit': { method: 'POST', path: '/api/tenant/change-requests' },
  'tenant.change_request.read': { method: 'GET', path: '/api/tenant/change-requests' },
  /* §11/§12. The office's own documents, and the two halves of a cancellation. */
  'tenant.invoice.read': { method: 'GET', path: '/api/tenant/invoices' },
  'tenant.invoice.detail': { method: 'GET', path: '/api/tenant/invoices/{document_uid}' },
  'tenant.subscription.cancel.preview': {
    method: 'GET',
    path: '/api/tenant/subscription/cancellation',
  },
  /* 0044 §הוספת מודול. Reading the shop is `billing.plan.read`; buying is its own capability. */
  'tenant.module_offer.read': { method: 'GET', path: '/api/tenant/module-offers' },
  'tenant.module.purchase': { method: 'POST', path: '/api/tenant/modules' },
  'tenant.subscription.cancel': {
    method: 'POST',
    path: '/api/tenant/subscription/cancellation',
  },
  'tenant.branding.read': { method: 'GET', path: '/api/tenant/branding' },
  'tenant.branding.update': { method: 'PUT', path: '/api/tenant/branding' },
  'tenant.connector.enrolment.mint': { method: 'POST', path: '/api/tenant/connectors/enrolment-tokens' },
  'tenant.connector.list': { method: 'GET', path: '/api/tenant/connectors' },
  'tenant.connector.revoke': { method: 'POST', path: '/api/tenant/connectors/{connector_id}/revocation' },
  'tenant.grant.create': { method: 'POST', path: '/api/tenant/grants' },
  'tenant.grant.read': { method: 'GET', path: '/api/tenant/grants' },
  'tenant.grant.revoke': { method: 'DELETE', path: '/api/tenant/grants' },
  'tenant.member.read': { method: 'GET', path: '/api/tenant/members' },
  'tenant.role.create': { method: 'POST', path: '/api/tenant/roles' },
  'tenant.role.read': { method: 'GET', path: '/api/tenant/roles' },
  'tenant.role.update': { method: 'PUT', path: '/api/tenant/roles/{role_code}' },
  // CM-012 / CM-058. The office's own view of support access into its data, and the two acts it
  // may take on it. The capabilities were seeded with zero routes while the security screen told
  // the office in writing that it could revoke at any moment.
  'security.support_grant.consent': { method: 'POST', path: '/api/tenant/support-grants/{grant_uid}/consent' },
  'security.support_grant.read': { method: 'GET', path: '/api/tenant/support-grants' },
  /*
   * X5 (R-7). The office's own MFA policy, under the EXISTING `tenant.settings.manage`.
   *
   * Declared here because `OPERATIONS` must equal the approved route baseline exactly
   * (`reachability.test.ts`) — not because a control calls it. Nothing does yet, and the reason is
   * recorded in `UNREACHED_HUMAN_ROUTES`: the panel the UX review specifies needs Hebrew the Master
   * does not define, and inventing product copy is forbidden.
   */
  /* 2026-11-03. The READ the write had no screen without — see `MfaPolicyPanel`. */
  'tenant.security.mfa_policy.read': { method: 'GET', path: '/api/tenant/security/mfa-policy' },
  'tenant.security.mfa_policy.set': { method: 'PUT', path: '/api/tenant/security/mfa-policy' },
  'security.support_grant.revoke': { method: 'POST', path: '/api/tenant/support-grants/{grant_uid}/revocation' },
  'tenant.source.configure': { method: 'PUT', path: '/api/tenant/sources/{connection_uid}/configuration' },
  'tenant.source.create': { method: 'POST', path: '/api/tenant/sources' },
  'tenant.source.external_company.list': { method: 'GET', path: '/api/tenant/sources/{connection_uid}/external-companies' },
  'tenant.source.external_company.resolve': { method: 'GET', path: '/api/tenant/sources/{connection_uid}/binding' },
  'tenant.source.health': { method: 'GET', path: '/api/tenant/sources/{connection_uid}/health' },
  'tenant.source.list': { method: 'GET', path: '/api/tenant/sources' },
  'tenant.source.probe': { method: 'POST', path: '/api/tenant/sources/{connection_uid}/connectivity-check' },
  'tenant.source.revoke': { method: 'POST', path: '/api/tenant/sources/{connection_uid}/revocation' },
  'tenant.user.disable': { method: 'POST', path: '/api/tenant/users/disable' },
  'tenant.user.invitation.resend': { method: 'POST', path: '/api/tenant/users/invitations/{invitation_uid}/resend' },
  /*
   * SECTION 7, 2026-10-28. What the inviter chose, recorded against a PENDING invitation and
   * applied at redemption. Three operations because they are three authorities: the role under
   * `tenant.role.manage`, the access under `companies.assignment.manage`, and the read-back of
   * what is still waiting under `tenant.user.manage`.
   */
  'tenant.invitation.role.record': { method: 'PUT', path: '/api/tenant/users/invitations/roles' },
  'tenant.invitation.access.record': { method: 'PUT', path: '/api/tenant/users/invitations/access' },
  'tenant.invitation.intent.read': {
    method: 'GET',
    path: '/api/tenant/users/invitations/{invitation_uid}/pending-access',
  },
  /*
   * THE LIST. **2026-10-28.** Same path as the invite, different verb — an invitation is a resource
   * of this collection, so POST creates one and GET returns them. The resend has existed since
   * CM-009 and was unreachable: no screen could name an invitation because nothing returned one.
   */
  'tenant.invitation.read': { method: 'GET', path: '/api/tenant/users/invitations' },
  /*
   * WHAT BECAME OF WHAT WAS CHOSEN FOR ONE PERSON. **2026-10-28.** Applied and refused together: a
   * refusal this screen did not show would be a refusal nobody can see.
   */
  'tenant.user.grant_outcome.read': {
    method: 'GET',
    path: '/api/tenant/users/{membership_uid}/grant-outcomes',
  },
  'tenant.user.invite': { method: 'POST', path: '/api/tenant/users/invitations' },
  'tenant.user.reactivate': { method: 'POST', path: '/api/tenant/users/reactivate' },
  'tenant.user.role.assign': { method: 'PUT', path: '/api/tenant/users/roles' },
  'tenant.user.session.read': { method: 'GET', path: '/api/tenant/users/{membership_uid}/sessions' },
  'tenant.user.session.revoke': { method: 'POST', path: '/api/tenant/users/{membership_uid}/sessions/revoke' },
  /* Section 1, 2026-10-29: the person's own details, and a change of address that is proved. */
  /* PUBLIC: followed from a mailbox, by somebody who may not be signed in. */
  'auth.email_change.confirm': { method: 'POST', path: '/api/auth/email-change' },
  'user.profile.read': { method: 'GET', path: '/api/me/profile' },
  'user.profile.update': { method: 'PUT', path: '/api/me/profile' },
  'user.email.change_request': { method: 'POST', path: '/api/me/email-change' },
  /* §6.9, 2026-10-29: the preferences that follow the account rather than the browser. */
  /* §6, 2026-10-29: the tasks module. Seven operations, and two of them CREATE. */
  'tenant.work_item.list': { method: 'GET', path: '/api/tenant/work-items/{scope}' },
  'tenant.work_item.read': { method: 'GET', path: '/api/tenant/work-items/{item_uid}/detail' },
  'tenant.work_item.create': { method: 'POST', path: '/api/tenant/work-items' },
  'companies.work_item.create': { method: 'POST', path: '/api/companies/{company_uid}/work-items' },
  'tenant.work_item.state': { method: 'PUT', path: '/api/tenant/work-items/{item_uid}/state' },
  'tenant.work_item.assign': { method: 'PUT', path: '/api/tenant/work-items/{item_uid}/assignee' },
  'tenant.work_item.comment': { method: 'POST', path: '/api/tenant/work-items/{item_uid}/comments' },
  'tenant.work_item.read_mark': { method: 'POST', path: '/api/tenant/work-items/{item_uid}/read' },
  /*
   * ── 0030/0031 · REPORTING DEADLINES. TWO SCOPES, AND THEY ARE NOT INTERCHANGEABLE. ──
   *
   * `tenant.reminder_rule.*` is what the OFFICE keeps: a report type, its period, its day and the
   * source the day came from. `companies.reminder_setting.*` is which of those THIS CLIENT files --
   * company in the PATH, so the gate resolves it, asserts the explicit grant and the lifecycle,
   * and refuses a uid from another office before the handler exists.
   */
  'tenant.reminder_rule.list': { method: 'GET', path: '/api/tenant/reminder-rules' },
  'tenant.reminder_rule.put': { method: 'PUT', path: '/api/tenant/reminder-rules' },
  'companies.reminder_setting.list': {
    method: 'GET',
    path: '/api/companies/{company_uid}/reminder-settings',
  },
  'companies.reminder_setting.put': {
    method: 'PUT',
    path: '/api/companies/{company_uid}/reminder-settings',
  },
  /*
   * ── 0032 · THE PUBLIC DOOR. FOUR ANONYMOUS OPERATIONS. ───────────────────────────────
   *
   * They are reached before any session exists, so none of them carries a company, a tenant or a
   * capability — and none of them is given one here. `public.signup.complete` is the only operation
   * in this table that creates an organisation.
   */
  'public.signup.plans': { method: 'GET', path: '/api/public/signup/plans' },
  /* ITEM 1. What one combination of a plan and the articles beside it costs. */
  'public.signup.quote': { method: 'POST', path: '/api/public/signup/quote' },
  'public.signup.start': { method: 'POST', path: '/api/public/signup/start' },
  'public.signup.verify': { method: 'POST', path: '/api/public/signup/verify' },
  'public.signup.payment_method': { method: 'POST', path: '/api/public/signup/payment-method' },
  'public.signup.complete': { method: 'POST', path: '/api/public/signup/complete' },
  /*
   * ── 0041 · THE PROVIDER-HOSTED PAYMENT PAGE. ─────────────────────────────────────────
   *
   * TWO ANONYMOUS, because a registration has no session and the leg that comes BACK from the
   * provider cannot have one either. THREE OF THE OFFICE'S OWN, for a card replacement.
   *
   * `public.payment.webhook` IS NOT HERE AND MUST NOT BE. It is Cardcom's server posting to us;
   * no browser calls it, and an operation in this table is a call this application makes.
   */
  /*
   * §11's THIRD AUTHORITY, which had no route at all until 0041. The list is
   * `billing.invoice.read`; this is `billing.invoice.download`, and it answers an ADDRESS rather
   * than bytes — the original lives in the provider's document archive.
   */
  'tenant.invoice.download': {
    method: 'GET',
    path: '/api/tenant/invoices/{document_uid}/document',
  },
  /* 0041. The clearing connection as an operator sees it: *"מצב החיבור, החיובים, הכשלים"*. */
  'platform.clearing.read': { method: 'GET', path: '/api/platform/clearing' },
  'public.payment.page': { method: 'POST', path: '/api/public/payment/page' },
  'public.payment.settle': { method: 'POST', path: '/api/public/payment/settle' },
  /*
   * ── THE WEBHOOK IS DECLARED HERE AND IS CALLED BY NOTHING IN THIS APPLICATION. ─────────────
   *
   * It has to be in this table because `reachability.test.ts` requires the table and the approved
   * route baseline to name EXACTLY the same set in both directions — a route absent from here is a
   * route the browser could not reach even if it should, and that is the defect the two-way check
   * exists to catch.
   *
   * It is `MACHINE_ROUTES`-classified, with the reason written there: its caller is Cardcom's own
   * server. A control in this application that posted to it would be this application pretending
   * to be the provider.
   */
  'public.payment.webhook': { method: 'POST', path: '/api/public/payment/webhook' },
  'tenant.payment_method.read': { method: 'GET', path: '/api/tenant/payment-method' },
  'tenant.payment_method.page': { method: 'POST', path: '/api/tenant/payment-method/page' },
  'tenant.payment_method.charges': {
    method: 'GET',
    path: '/api/tenant/payment-method/charges',
  },
  /* §4, 2026-10-29: the market readings. Public information, served to any signed-in session. */
  'user.market.read': { method: 'GET', path: '/api/me/market' },
  'user.preference.read': { method: 'GET', path: '/api/me/preferences' },
  'user.preference.update': { method: 'PUT', path: '/api/me/preferences' },
  'tenant.user.update': { method: 'PUT', path: '/api/tenant/users/{user_uid}/profile' },
  'user.session.read': { method: 'GET', path: '/api/me/sessions' },
  'user.session.revoke_all': { method: 'POST', path: '/api/me/sessions/revoke' },
} as const satisfies Readonly<Record<string, OperationSpec>>;

export type OperationId = keyof typeof OPERATIONS;

export const OPERATION_IDS: readonly OperationId[] = Object.keys(OPERATIONS) as OperationId[];

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// THE REQUEST SHAPES
//
// One entry per operation this app is permitted to call. An operation absent from here CANNOT be
// called — see `UndeclaredBody` — which is why wiring a new action is a deliberate act that states
// what it sends, and why `reachability.test.ts` can reconcile every field name below against the
// handler that reads it.
// ══════════════════════════════════════════════════════════════════════════════════════════════════

/**
 * WHO THE SERVER SAYS THE CALLER IS. **CM-030.**
 *
 * Field for field what `services/session-identity.ts` returns. This is the ONLY thing the browser
 * derives its surface from — CM-014 removed the two mechanisms that competed with it, an address
 * that minted a session and a button that set one.
 */
export interface WhoamiResponse {
  readonly userUid: string;
  readonly displayName: string;
  /** `'platform' | 'tenant'`. Fixed for the life of the account, and NOT the surface. */
  readonly accountKind: string;
  /** The SESSION's context. A platform account under a support grant is in `TENANT`. */
  readonly context: string;
  readonly tenantUid: string | null;
  readonly tenants: readonly TenantSummary[];
  readonly capabilities: readonly string[];
  readonly supportGrantUid: string | null;
  /** The gate's MFA mandate, reported so the shell can route to enrolment rather than to a wall. */
  readonly mfaEnrolmentRequired?: boolean;
  /**
   * WHERE A PERSON REACHES A PERSON, OR `null`. **2026-11-03.**
   *
   * OPTIONAL ON THE WIRE and non-optional on `SessionSummary`, which is the same shape
   * `mfaEnrolmentRequired` already has and for the same reason: a server that predates the field
   * answers without it, and `?? null` below turns that into the state the panel renders — *"no
   * support address is configured"* — rather than `undefined` reaching a `mailto:`.
   */
  readonly supportContactAddress?: string | null;
}

/**
 * The wire answer, as the shell's `SessionSummary`.
 *
 * A translation and not a widening: `context` is narrowed to the two surfaces this product has, and
 * anything else becomes `null` — an unrecognised context is not a surface, and defaulting it to
 * TENANT would grant one. That is the CM-014 defect in miniature, so it is refused here too.
 */
export function sessionFromWhoami(me: WhoamiResponse): SessionSummary | null {
  const context = me.context === 'PLATFORM' || me.context === 'TENANT' ? me.context : null;
  if (context === null) return null;
  return {
    userUid: me.userUid,
    displayName: me.displayName,
    sessionContext: context,
    tenants: me.tenants,
    activeTenantUid: me.tenantUid,
    capabilities: me.capabilities,
    supportGrantUid: me.supportGrantUid,
    /* 2026-11-03. Carried through unchanged; `null` is an estate with no address configured. */
    supportContactAddress: me.supportContactAddress ?? null,
    /*
     * ABSENT MEANS NOT REQUIRED, and that is the safe default in this direction: this flag only ever
     * turns a working workspace into an enrolment screen, so an older server that does not send it
     * must not strand the browser on one. The server is the thing enforcing; a `false` here cannot
     * open anything it is refusing.
     */
    mfaEnrolmentRequired: me.mfaEnrolmentRequired === true,
  };
}

/** The four-arm answer to a sign-in. `REFUSED` never arrives — the route answers it as a 401. */
export type SignInResponse =
  | { readonly outcome: 'SESSION' }
  | { readonly outcome: 'MFA_REQUIRED'; readonly challengeRef: string; readonly expiresAt: string }
  | { readonly outcome: 'MFA_ENROLMENT_REQUIRED'; readonly challengeRef: string; readonly expiresAt: string };

export interface OperationBodies {
  /**
   * Section 9.1(5): the operator confirms the update once. The body is the ARTIFACT HASH and
   * nothing else -- no host, no path, no release directory, no credential. Everything else about
   * whether those bytes may be installed is re-derived on the host from a registry this
   * application cannot write.
   */
  'platform.release.update.start': { readonly artifactSha256: string };
  /**
   * Section 9.1(7): bound to the installed DEPLOYMENT RECORD, with evidence. The artifact hash is
   * deliberately NOT a field: the server reads it from the deployment being accepted, because a
   * caller-supplied hash could name something that was never installed here.
   */
  'platform.release.accept': { readonly deploymentUid: string; readonly evidence: string };
  'auth.session.create': { readonly login: string; readonly password: string };
  /** Takes nothing. The subject is the session holder, and there is no field that could name another. */
  /**
   * `challengeRef` is the ENROL challenge issued at sign-in, and it is OPTIONAL because the route
   * takes two proofs of the first factor and no third: a bound session, or this ref
   * (`routes/session.ts:112-114`). A PLATFORM principal holds no cookie at that moment -- the
   * MFA_ENROLMENT_REQUIRED arm deliberately sets none -- so for a platform owner this ref is the
   * ONLY proof available, and the type was `Record<string, never>`: a body with no fields, which
   * made the one reachable arm unexpressible. The mirror test could not catch it, because a
   * declared body with no fields has no field to reconcile.
   */
  'auth.mfa.enrol': { readonly challengeRef?: string };
  'auth.mfa.enrolment.confirm': { readonly challengeRef: string; readonly code: string };
  /** The code, and nothing else: the subject is the bound session. */
  'auth.stepup.assert': { readonly code: string };
  /**
   * `factor` is REQUIRED and is never inferred from the shape of the code.
   *
   * The handler says why: trying TOTP and then a recovery code on one submission spends two
   * rate-limit budgets and makes the RESPONSE TIME say which one ran. The caller states which
   * factor the person is presenting, and one submission tries exactly one thing.
   */
  /* The invitee CHOOSES the credential. Nothing is generated, echoed or returned — this is what
     replaces Legacy create-user, which hard-coded 'changeme' and put it in the JSON response. */
  'auth.invitation.accept': { readonly token: string; readonly password: string };
  /* A reset re-establishes the FIRST factor only, and issues no session on any branch. */
  'auth.password.reset_complete': { readonly token: string; readonly password: string };
  /* The address only. The answer is identical whether or not it is known — anything else would be
     an account-enumeration oracle, and the token reaches the MAILBOX, never a response body. */
  'auth.password.reset_request': { readonly login: string };
  /* `logoAssetRef` is OPTIONAL and may be cleared. The handler reads a non-string as `null`. */
  'tenant.branding.update': {
    readonly displayNameHe: string;
    readonly logoAssetRef?: string | null;
  };
  'auth.mfa.verify': {
    readonly challengeRef: string;
    readonly code: string;
    readonly factor: 'totp' | 'recovery_code';
  };
  /*
   * Section 1, 2026-10-29. EXACTLY THE TWO FIELDS the route serves: the server reads every key at
   * run time and refuses an unknown one BY NAME, so a third field declared here would compile and
   * then be refused by the estate -- which is the drift `reachability.test.ts` reconciles against
   * the handler.
   */
  /* ONE FIELD, and deliberately no identifier: the account comes from the row the token found. */
  'auth.email_change.confirm': { readonly token: string };
  'user.profile.update': { readonly displayName: string; readonly phone: string | null };
  /* One field. There is deliberately no identifier: the subject is the signed-in person. */
  'user.email.change_request': { readonly newEmail: string };
  /*
   * ONE KEY AND ITS VALUE. `key` is typed as the closed vocabulary -- the same two strings the
   * server holds in `PREFERENCE_KEYS` -- so a misspelt preference is a compile error here and a
   * named refusal there. `value` is `readonly string[]` because both preferences that exist today
   * are panel orders; the server validates the shape at run time regardless, since a type is not
   * a control over a request the browser did not send.
   */
  'user.preference.update': {
    readonly key: PreferenceKey;
    readonly value: readonly string[];
  };
  /*
   * ── THE TASKS MODULE'S FOUR WRITE SHAPES. **§6, 2026-10-29.** ──────────────────────
   *
   * `companyUid` IS ABSENT FROM BOTH CREATE SHAPES, deliberately and not by omission. An item about
   * one client file is raised through `companies.work_item.create`, whose PATH carries the company
   * so the gate resolves it, asserts the grant and the lifecycle, and refuses a uid from another
   * office. A `companyUid` in the body is refused by the server BY NAME -- it was on the served
   * list for one draft, which meant a body naming a company was accepted and silently ignored.
   *
   * `expectedState` IS REQUIRED on a move and is not a convenience: it reaches the store's own
   * predicate, so one person pressing *done* twice on a stale screen is told their screen is old
   * rather than overwriting whatever happened in between.
   */
  'tenant.work_item.create': {
    readonly title: string;
    readonly itemKind?: 'task' | 'notice';
    readonly body?: string | null;
    readonly moduleCode?: string | null;
    readonly assignToMembershipUid?: string | null;
    readonly dueOn?: string | null;
  };
  /*
   * `companies.work_item.create` AND `tenant.work_item.assign` DECLARE NO BODY YET, deliberately.
   *
   * `reachability.test.ts` refuses a request shape for an operation nothing calls, and it is right
   * to: a declared shape is a claim that this app sends it, reconciled field by field against the
   * handler that reads it. Neither has a caller in this round -- the company-scoped create belongs
   * on the company screen and `assign` needs a member picker on the administration surface -- so
   * the shapes arrive WITH their callers rather than ahead of them.
   */
  'tenant.work_item.state': {
    readonly expectedState: WorkItemState;
    readonly toState: WorkItemState;
  };
  'tenant.work_item.comment': { readonly body: string };
  /*
   * ── THE RULE SHAPE, AND WHY EVERY FIELD IS REQUIRED HERE WHILE THE SERVER TREATS MOST AS
   *    OPTIONAL. ──
   *
   * The form sends the whole rule on every save, because this is a PUT keyed on `ruleCode`: an
   * office keeps ONE answer per report type, and a partial body would make "the day is now null"
   * indistinguishable from "the day was not in this request". The server reads each key at run
   * time and refuses an unknown one by name.
   *
   * `verificationState: 'verified'` is refused unless `dueDayOfPeriod`, `sourceNote` and
   * `effectiveFrom` are all present -- `needs_day`, `needs_source`, `needs_effective_from` -- and
   * `isActive` is refused on an unverified rule. Those are the Owner's
   * *"מה שלא אומת יישאר מסומן ולא פעיל"* as four named refusals rather than a 500 from a CHECK.
   */
  'tenant.reminder_rule.put': {
    readonly ruleCode: string;
    readonly displayNameHe: string;
    readonly periodKind: string;
    readonly dueDayOfPeriod: number | null;
    readonly dueMonthOffset: number;
    readonly leadDays: number;
    readonly sourceNote: string | null;
    readonly isActive: boolean;
    readonly verificationState: 'unverified' | 'verified';
    readonly effectiveFrom: string | null;
    readonly effectiveTo: string | null;
  };
  /*
   * NO `companyUid`. The company travels in the path and the server reads it from `ctx.companyId`;
   * a company in the body would be a second answer to a question the gate already answered.
   */
  /*
   * ── THE PUBLIC DOOR'S THREE WRITE SHAPES. ────────────────────────────────────────────
   *
   * NO PRICE ON ANY OF THEM, deliberately. §9.6's figures are shown by `public.signup.plans` and
   * re-resolved by the server at `complete`; a price travelling back from the browser would be a
   * price the browser chose, and the consent record would be evidence of nothing.
   *
   * NO PASSWORD EITHER. The first administrator sets one through `auth.invitation.accept`, which
   * stays the product's one credential writer — so there is no field here through which a
   * credential could be sent, and that is the whole reason the journey ends in an invitation.
   */
  'public.signup.start': {
    readonly email: string;
    readonly legalName: string;
    readonly displayName?: string | null;
  };
  /** The token and nothing else: the subject is the row the token identifies. */
  'public.signup.verify': { readonly token: string };
  /**
   * STEP FOUR. ו-16: *"בדיקת אמצעי תשלום אצל הספק, ורק אז תחילת הניסיון ללא חיוב."*
   *
   * `providerHandle` is the ONE-TIME reference the provider's own page gave this browser after the
   * customer typed their card THERE. There is no card field on this body and no amount: the Owner's
   * *"פרטי הכרטיס המלאים ו־ CVV לא יישמרו במערכת שלנו"* and *"אין לבצע עסקה אמיתית לצורך
   * בדיקה"*, as a shape rather than as a rule.
   */
  'public.signup.payment_method': {
    readonly signupUid: string;
    readonly providerHandle: string;
  };
  /**
   * THE PAGE REQUEST. A registration uid and a plan code — AND NO FIGURE.
   *
   * Cardcom requires an `Amount` even for a card validation, and the server resolves it from the
   * published price book. A price in this body would be a price the browser chose: *"לא רוצה
   * לראות מחיר בקוד אלא תחשיב לפי שדה להזנה שלי"* cuts both ways — not a literal in source, and not a
   * number from a client either.
   */
  'public.payment.page': { readonly signupUid: string; readonly planCode: string };
  /**
   * THE RETURN LEG. One reference, which this application received in its own URL.
   *
   * It is a POINTER and not a result: there is no field here through which a browser could claim
   * that a card was accepted. The server finds the page the reference names and asks Cardcom.
   */
  'public.payment.settle': { readonly reference: string };
  /** A card replacement takes nothing: the office is the session's, and the plan is the office's. */
  'tenant.payment_method.page': Record<string, never>;
  /**
   * ONE ARTICLE CODE, AND NO PRICE AND NO QUANTITY.
   *
   * *"לא רוצה לראות מחיר בקוד אלא תחשיב לפי שדה להזנה שלי"* cuts both ways, and this is the
   * second way: the server resolves the price from the office's own book, so there is no field
   * here through which a browser could name what it is willing to pay. The quantity is the office
   * itself — every opening article is charged per משרד — so there is nothing to choose.
   */
  'tenant.module.purchase': { readonly skuCode: string };
  'public.signup.complete': {
    readonly signupUid: string;
    readonly planCode: string;
    /**
     * THE ARTICLES CHOSEN BESIDE THE PLAN — CODES ONLY. **ITEM 1, 2026-11-03.**
     *
     * NO PRICE AND NO QUANTITY. The server resolves each code against the chosen plan's own offer
     * and prices it from the published book, so there is no field here through which a browser
     * could name what it is willing to pay — the same rule `tenant.module.purchase` states above.
     */
    readonly skuCodes: readonly string[];
    readonly tenantKind: string;
    /** §9.6. Explicit, and the server refuses without it. */
    readonly consentGiven: true;
  };
  /** ITEM 1. The codes only, for the same reason, and it writes nothing. */
  'public.signup.quote': { readonly planCode: string; readonly skuCodes: readonly string[] };
  /**
   * ITEM 4. A PLAN CODE AND NOTHING ELSE — no tenant and no price.
   *
   * The tenant comes from the caller's bound context, so there is no field through which an
   * office could move somebody else's subscription; and the price is the published book's, so
   * there is none through which it could name what it is willing to pay.
   */
  'tenant.subscription.plan.change': { readonly planCode: string };
  'companies.reminder_setting.put': {
    readonly ruleUid: string;
    readonly isEnabled: boolean;
    readonly periodKind: string | null;
    readonly dueDayOfPeriod: number | null;
    readonly dueMonthOffset: number | null;
    readonly leadDays: number | null;
    readonly settingsNote: string | null;
  };
  /** Takes nothing. Reading is not handling, and there is no value a caller could substitute. */
  /*
   * NO BODY, AND THAT IS THE AUTHORISATION. 0046. The subscription being charged is read from the
   * caller's own bound context -- `only` is a filter the run honours, so a uid in a body field
   * would charge the neighbour. There is no field here to be given the wrong value in.
   */
  /*
   * 0047. A KIND AND THE CUSTOMER'S OWN WORDS, and NO AMOUNT: a figure here would be a
   * customer proposing their own price into a record an operator reads as one (08 §8.4).
   */
  'tenant.change_request.submit': { readonly requestKind: string; readonly detail: string };
  /*
   * `expectedStatus` COMES FROM THE ROW THE OPERATOR IS LOOKING AT and reaches the WHERE, so
   * two people answering one request produce one answer and one refusal rather than the
   * second overwriting what the customer was told.
   */
  /*
   * A GROSS FIGURE AS A FIXED-POINT STRING, and a REASON — *"עם אישור מפורש, סיבה
   * ותיעוד"*. Never a number: a price that passed through a JS number is a rounded price,
   * and the server compares this against a constraint that will not round with us.
   */
  'platform.billing.credit.issue': { readonly amountTotal: string; readonly reason: string };
  'platform.change_request.resolve': {
    readonly expectedStatus: string;
    readonly toStatus: string;
    readonly resolution: string | null;
  };
  /*
   * X5/R-7. THE POLICY VALUE IS A `string` ON PURPOSE, mirroring the route's own reasoning:
   * it is refused by `isTenantMfaPolicy` at the store -- `CK_tenant_mfa_policy`'s own set,
   * one guard shared with the sign-in read -- rather than by a second vocabulary restated
   * here, which would be free to drift from the constraint.
   */
  'tenant.security.mfa_policy.set': { readonly policy: string };
  /*
   * 0027/2026-11-03. `null` CLEARS THE ASSIGNMENT and is not an omission: `assign` reads the field
   * as nullable and makes the item nobody's, which an office needs reachable once somebody has
   * been named. The uid is a MEMBERSHIP uid resolved by the service against the caller's own
   * tenant -- `assignee_unknown` for anybody else -- so this field names a person inside the
   * office and nothing wider.
   */
  /*
   * §6.11. THE COMPANY IS IN THE PATH AND NOT IN THIS BODY, which is the whole difference from
   * the tenant-scoped create: the gate's stage 4 resolves `company_uid` -- the explicit grant, the
   * access level, the lifecycle, the ownership -- before the handler exists. A `companyUid` field
   * here would be the product answering *may this caller see that company* a second time.
   */
  'companies.work_item.create': {
    readonly title: string;
    readonly body: string | null;
    readonly dueOn: string | null;
  };
  'tenant.work_item.assign': { readonly membershipUid: string | null };
  'tenant.work_item.read_mark': Record<string, never>;
  'tenant.user.update': { readonly displayName: string; readonly phone: string | null };
  'user.session.revoke_all': Record<string, never>;
  /** Ends THIS session. Takes nothing — the session is the cookie, and there is no field for one. */
  'auth.session.delete': Record<string, never>;

  // CM-012 / CM-058. Consent names no grant in its body — the grant is the path, and the office is
  // the bound context. There is no field here through which one office could consent for another.
  'security.support_grant.consent': Record<string, never>;
  'security.support_grant.revoke': { readonly reason: string };

  /**
   * X3 — A NEW PLAN. `status` is sent explicitly and is never defaulted here: the vocabulary is
   * `CK_plan_status`, the server refuses anything outside it, and a browser choosing `active` on the
   * operator's behalf would be a lifecycle decision made by a form. NO PRICE FIELD (D-X3-1).
   */
  'platform.catalogue.plan.create': {
    readonly code: string;
    readonly displayNameHe: string;
    readonly displayNameEn: string;
    readonly status: string;
  };
  'platform.catalogue.sku.create': {
    readonly code: string;
    readonly displayNameHe: string;
    readonly displayNameEn: string;
    readonly chargeDimensionCode: string;
    readonly isSellable: boolean;
    readonly status: string;
    /**
     * `volume` | `graduated`. 0040, and REQUIRED — `tier_mode` is NOT NULL with no default.
     *
     * A field the client could omit would have the server choose which units a tier price applies
     * to, and the two answers differ by a hundred shekels on one office.
     */
    readonly tierMode: string;
  };
  /** ONE module per submit. No multi-select exists in this product, and none is invented here. */
  'platform.catalogue.sku.module.attach': { readonly moduleCode: string };
  /** `includedQuantity` is required. `CK_plan_sku_qty` admits `>= 0`, and zero is a real inclusion. */
  'platform.catalogue.plan.sku.include': {
    readonly skuCode: string;
    readonly includedQuantity: number;
  };
  /**
   * THE COMPOSITION EDITORS, AND THE ACKNOWLEDGEMENT IS NOT OPTIONAL. **Owner, 2026-10-21:**
   * *"ההשפעה על מנויים קיימים חייבת להיות מפורשת ומבוקרת."*
   *
   * `acknowledgeLiveSubscriptions` is the number the operator was SHOWN, sent back and compared
   * against a fresh measurement. A caller that never read the impact cannot change the composition,
   * and a stale number is refused with the current one so the screen can ask again.
   */
  'platform.catalogue.plan.sku.quantity': {
    readonly includedQuantity: number;
    readonly acknowledgeLiveSubscriptions: number;
  };
  'platform.catalogue.plan.sku.exclude': { readonly acknowledgeLiveSubscriptions: number };
  'platform.price_book.create': {
    readonly code: string;
    readonly displayNameHe: string;
    readonly displayNameEn: string;
    readonly currencyCode: string;
  };
  /**
   * PUBLISHING TAKES NO FIELDS. The act is "this book is now the list price", and the book is the
   * address. A body carrying a date or a flag would be a second way to say the same thing.
   */
  'platform.price_book.publish': Record<string, never>;
  /* NO BODY. The plan is whatever the subscription is on; naming one here would be a second
     way to change it, and `platform.subscription.plan.assign` owns that. */
  'platform.subscription.plan.apply': Record<string, never>;
  /** THE REASON IS A FIELD, not an optional note: the database refuses a change without one. */
  'platform.policy.trial_days.set': { readonly days: number; readonly reason: string };
  /*
   * AN EMPTY SELECTION IS REFUSED BY THE SERVER AND IS NOT EXPRESSIBLE AS A DEFAULT HERE: the
   * field is required, so a caller cannot omit it and be read as "all".
   */
  'platform.billing.run.execute': {
    readonly subscriptionUids: readonly string[];
    readonly reason: string;
  };
  /** ONE office, by uid. The review is per customer; a sweep is the timer's, not a button's. */
  'platform.dunning.review': { readonly tenantUid: string };
  /**
   * THE DAY COUNTS, DATED FORWARD. There is no id and no PUT: the store has no update method,
   * because a cycle judged under last month's thresholds was judged by them and an edited row
   * cannot say so.
   */
  'platform.dunning.policy.set': {
    readonly effectiveFrom: string;
    readonly warnAfterDays: number;
    readonly restrictAfterDays: number;
    readonly terminateAfterDays: number;
    readonly noticeIntervalDays: number;
  };
  /**
   * §10ב. The public door. `open` is a BOOLEAN and the route refuses anything else -- `"false"`
   * and `0` are what a hand-written client sends, and coercing either would decide whether
   * strangers may create tenants from a guess about intent. The reason is required for the trial
   * length's reason and more so.
   */
  'platform.signup_door.set': { readonly open: boolean; readonly reason: string };
  /**
   * §10ב, the SECOND lock. No reason field, and that is deliberate rather than an omission:
   * `billing.plan` carries no reason column, and the act is already attributable -- the audit row
   * names the actor, the plan code and the direction (`..._opened` / `..._closed`).
   */
  'platform.catalogue.plan.public_signup.set': {
    readonly planCode: string;
    readonly open: boolean;
  };
  /** `priceBookUid: null` CLEARS the pin. Sent explicitly, never by omitting the field. F1R-04. */
  'platform.subscription.price_book.set': {
    readonly priceBookUid: string | null;
    readonly reason: string;
  };
  'platform.price_book.item.add': {
    readonly skuCode: string;
    readonly planCode: string | null;
    readonly tierFrom: number;
    readonly tierTo: number | null;
    /** A STRING. See `PriceItemSummary.unitPrice`. */
    readonly unitPrice: string;
    readonly effectiveFrom: string;
  };
  'platform.tenant.create': { readonly slug: string; readonly legalName: string; readonly kind: string };
  'platform.subscription.create': {
    readonly planCode: string;
    readonly periodKind: string;
    /**
     * THE TRIAL INTENT, NOT A DATE THE BROWSER COMPUTED. FND-08.
     *
     * `'default'` asks the server to apply the Owner's persisted policy; `'none'` sells outright;
     * a `YYYY-MM-DD` is an explicit authorised window. The console sends `'default'`, which is the
     * whole point: the number lives in one place and the browser is not it.
     */
    readonly trial: 'default' | 'none' | string;
  };
  'platform.subscription.sku.add': { readonly skuCode: string; readonly quantity: number };
  'platform.tenant.activate': { readonly email: string; readonly displayName: string };

  'platform.tenant.suspend': { readonly reason: string };
  'platform.tenant.reactivate': { readonly reason: string };
  'platform.tenant.close': { readonly reason: string };

  'platform.tenant.offboarding.terminate': {
    readonly reason: string;
    readonly exportDisposition: ExportDispositionBody;
  };
  'platform.tenant.offboarding.reinstate': { readonly reason: string };

  'platform.subscription.plan.assign': { readonly planCode: string; readonly reason: string };
  'platform.subscription.state.change': { readonly toState: string; readonly reason: string };
  /*
   * BOTH ENDS OF THE WINDOW. **Found by rendering the screen, 2026-10-23.**
   *
   * `trialStartsAt` was missing from this declaration, and the route reads it with `nullableStr`,
   * which tells ABSENT from `null`. So every call this client could type-check was answered with
   * `invalid_request` before the service ran: the contract described a body the server refuses.
   * `CK_subscription_trial` is the rule — both dates or neither — and it is now sayable here.
   */
  'platform.trial.set': {
    readonly trialStartsAt: string | null;
    readonly trialEndsAt: string | null;
    readonly reason: string;
  };
  'platform.trial.extend': { readonly trialEndsAt: string; readonly reason: string };
  'platform.limit.company.set': { readonly limit: number | null; readonly reason: string };
  'platform.limit.seat.set': { readonly limit: number | null; readonly reason: string };

  'platform.discount.apply': DiscountBody;
  'platform.discount.apply_full': DiscountBody;
  'platform.discount.approve': Record<string, never>;

  'platform.support_grant.request': {
    readonly reason: string;
    readonly ticketRef: string | null;
    readonly mode: string;
    readonly requiresConsent: boolean;
    readonly capabilitySet: readonly string[];
    readonly isRelatedParty: boolean;
  };
  'platform.support_grant.approve': Record<string, never>;
  'platform.support_grant.revoke': { readonly reason: string };

  'companies.company.create': {
    readonly legalName: string;
    readonly displayName: string;
    readonly entityKind: string;
    readonly taxId: string | null;
  };
  'companies.company.update': {
    readonly legalName: string;
    readonly displayName: string;
    readonly entityKind: string;
    readonly taxId: string | null;
  };
  'companies.company.lifecycle': { readonly to: string; readonly mergedIntoCompanyUid?: string };
  /**
   * PRESENTATION, NEVER PERMISSION. The handler reads `membershipUid` and `kind` — a partner and the
   * sort of relationship, not a free-text code. The screen sent `{portfolioCode}` until this table
   * made the mismatch a compile error.
   */
  'companies.portfolio.assign': { readonly membershipUid: string; readonly kind: string };
  'companies.source_binding.create': {
    readonly connectionUid: string;
    readonly externalCompanyId: string;
    readonly confidence: string;
    readonly note: string;
    readonly taxIdCheckDigitValid: boolean | null;
  };
  /**
   * ONE FIELD, AND IT IS A CODE FROM THE SERVER'S OWN LIST. Owner finding 6.
   *
   * No `connectionUid`, no external identifier, no status: a selection says WHICH system keeps this
   * company's books and claims nothing about having reached it --
   * *"בלי מזהה ספק מומצא ובלי להציג ״מחובר״ לפני בדיקה"*. No `companyUid` either, because the
   * company is in the address and the gate resolves it inside the caller's tenant.
   */
  'companies.source_system.select': { readonly systemCode: string };

  /*
   * TWO FIELDS, AND NEITHER IS A SECRET OR AN ENDPOINT. **2026-10-26.**
   *
   * A connection is PROVISIONED before it is CONFIGURED, so that `NOT_CONFIGURED` is a state the
   * row can hold rather than a gap somebody fills with a default -- 07 N-5, where a missing
   * `data_source_type` became a working default on 5 of 313 companies because nothing was willing
   * to hold the absence. The handler reads exactly these two and answers 400 to anything else.
   */
  'tenant.source.create': { readonly displayNameHe: string; readonly sourceSystemCode: string };
  'tenant.source.configure': SourceConfigureBody;
  'tenant.source.probe': Record<string, never>;

  'tenant.grant.create': {
    readonly membershipUid: string;
    readonly companyUids: readonly string[];
    readonly accessLevel: string;
  };
  'tenant.grant.revoke': { readonly membershipUid: string; readonly companyUid: string };

  'tenant.role.create': {
    readonly roleCode: string;
    readonly displayNameHe: string;
    readonly capabilities: readonly string[];
  };
  'tenant.role.update': { readonly displayNameHe: string; readonly capabilities: readonly string[] };

  /**
   * §12. THE THREE FIELDS, AND THE REASON EACH ONE IS SEPARATE.
   *
   * `confirmation` is the word typed in capitals, compared exactly by the service — not a boolean,
   * because a boolean is what a single click sends and *"לא בלחיצה אחת"* is the requirement.
   * `reason` is one of five codes and is mandatory. `comment` is the customer's own words and is
   * nullable: *"טקסט חופשי לא חובה."*
   *
   * `reachability.test.ts` reconciles these three names against the fields the handler in
   * `routes/tenant-admin.ts` actually reads, so a renamed field here is a compile-and-test failure
   * rather than a cancellation that silently refuses with `confirmation_mismatch`.
   */
  'tenant.subscription.cancel': {
    readonly confirmation: string;
    readonly reason: string;
    readonly comment: string | null;
  };
  'tenant.user.role.assign': { readonly membershipUid: string; readonly roleCodes: readonly string[] };

  'tenant.user.invite': { readonly displayName: string; readonly email: string };
  'tenant.invitation.role.record': {
    readonly invitationUid: string;
    readonly roleCodes: readonly string[];
  };
  'tenant.invitation.access.record': {
    readonly invitationUid: string;
    readonly companyUids: readonly string[];
    readonly accessLevel: string;
  };
  'tenant.user.disable': { readonly membershipUid: string; readonly reason: string };
  'tenant.user.reactivate': { readonly membershipUid: string; readonly reason: string };
  'tenant.user.invitation.resend': Record<string, never>;
  'tenant.user.session.revoke': { readonly reason: string };
}

/**
 * ALL SIX FIELDS ARE REQUIRED BY THE HANDLER, AND EVERY ONE OF THEM IS OPTIONAL HERE.
 *
 * That is not a contradiction; it is the refusal contract. `tenant.source.configure` answers
 * `invalid_request` unless all six are present, and `SourceConnectionScreen` renders the server's
 * list of everything that was missing (07 N-5: never the first only). If this type required them,
 * `configurationBody` would have to invent an empty string for an unfilled control — which is the
 * defect 07 N-5 records, a missing `data_source_type` becoming a working default on 5 of 313
 * companies because nothing was willing to hold the absence.
 *
 * There is no `password`, no `token` and no `connectionString`, matching the handler exactly.
 */
export interface SourceConfigureBody {
  readonly displayNameHe?: string;
  readonly sourceSystemCode?: string;
  readonly endpointRef?: string;
  readonly secretRef?: string;
  readonly textEncoding?: string;
  readonly sourceDateFormat?: string;
}

/** A discount, in the two shapes it is applied in. `apply_full` is a separate ACTION, not a bigger number. */
export interface DiscountBody {
  readonly discountKind: string;
  /** A percentage is a number. An AMOUNT is a string and stays one — 0003. */
  readonly discountPct: number | null;
  readonly discountAmount: string | null;
  /**
   * ── THE REDUCED PRICE. 0037, AND IT COULD NOT BE SENT. **2026-11-01.** ─────────────────────
   *
   * Migration 0037 added `fixed_price` for the Owner's *"מחיר קבוע מופחת לתקופה, כגון 50 ש״ח
   * לחודש למשך חודשיים"*, `CommercialService` validates it, `parseTerm` reads it off the body
   * and `priceCycle` applies it. **This interface had no field for it**, so the one surface that
   * applies a benefit could not ask for the kind the Owner asked for by name.
   *
   * A STRING, like `discountAmount` and for the same reason: a float here defeats fixed-point
   * money at the first `JSON.parse`, before any validator sees it.
   */
  readonly fixedPriceAmount: string | null;
  readonly currencyCode: string | null;
  readonly scope: string;
  readonly scopeSkuCode: string | null;
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
  readonly reason: string;
  /** DECLARED, never defaulted. OA-0002. */
  readonly isRelatedParty: boolean;
}

/** No arm for "not answered". A termination request with no disposition is not built. */
export type ExportDispositionBody =
  | { readonly kind: 'REQUESTED' }
  | { readonly kind: 'ALREADY_DELIVERED'; readonly exportRequestUid: string }
  | { readonly kind: 'WAIVED'; readonly detail: string };

/** The answers this app reads. An operation absent from here answers `void`. */
export interface OperationResults {
  'auth.session.create': SignInResponse;
  /**
    * CM-024. The declared result was `{ challengeRef }` alone, so the screen had nothing to show and
    * rendered a FIXTURE SECRET instead — a fixed, published Base32 string, offered to every user as
    * the key to put in their authenticator, while the confirmation went to the server's real
    * challenge. Enrolment could never verify, and anyone who scanned it enrolled a shared secret.
    *
    * The server has always returned `provisioning`. Nothing needed to be built on that side; what
    * was missing was this declaration and a screen that read it.
    */
  'auth.mfa.enrol': {
    readonly challengeRef: string;
    readonly expiresAt: string;
    /** The `otpauth://` URI. **IT CONTAINS THE SHARED SECRET.** Displayed once, stored nowhere. */
    readonly provisioning: string;
  };
  /** The recovery codes exist only AFTER a code proves the secret reached the device. */
  'auth.mfa.enrolment.confirm': { readonly outcome: string; readonly recoveryCodes: string };
  'auth.stepup.assert': { readonly outcome: string };
  /* Success is a SESSION — the cookie is set by the response, and this body only names the outcome. */
  'auth.mfa.verify': { readonly outcome: string };
  'auth.invitation.accept': { readonly ok: boolean };
  'auth.password.reset_complete': { readonly accepted: boolean };
  /* Always the same body, whether or not the address is known. Deliberately carries nothing. */
  'auth.password.reset_request': { readonly requested: boolean };
  /**
   * WHAT IS STORED. `null` = the office has not set a name -- NEVER "the save failed",
   * which is a different statement and one this answer cannot make.
   */
  'tenant.branding.read': { readonly displayNameHe: string | null };
  'tenant.branding.update': { readonly ok: boolean };
  'auth.whoami.read': WhoamiResponse;
  'auth.session.delete': { readonly ok?: boolean };
  /* Phase 2 §13 — the reads screens used to render from fixtures. Shapes mirror `contract.ts`. */
  'tenant.member.read': { readonly members: readonly MembershipSummary[] };
  'tenant.role.read': { readonly roles: readonly RoleSummary[] };
  'tenant.grant.read': { readonly grants: readonly CompanyGrantSummary[] };
  'companies.list.read': { readonly companies: readonly CompanySummary[] };
  'companies.register.read': { readonly companies: readonly CompanySummary[] };

  /*
   * THE CONNECTIONS THE OFFICE HAS DEFINED. **Declared 2026-10-26, and the screen read FIXTURES
   * until that day.**
   *
   * `SourceConnectionsScreen` rendered `fixturesEnabled() ? fixtureSourceConnections : []`, so on
   * a real estate the list was ALWAYS EMPTY and the office was told it had connected nothing --
   * whatever it had connected. The route existed, the service decided, and nothing called it.
   */
  'tenant.source.list': { readonly connections: readonly SourceConnectionSummary[] };
  /** 201. The body states the NEXT STEP: every field the office must now supply, all at once. */
  'tenant.source.create': {
    readonly connectionUid: string;
    readonly state: string;
    readonly missingConfiguration: readonly string[];
  };
  /** CONTACT, never data freshness -- and the response says so in a field, not in a comment. */
  'tenant.source.health': ConnectorHealthSummary;
  /**
   * `unmappedCount` is 07 L-3's onboarding queue as a NUMBER ON THE RESPONSE, rather than a
   * `_meta.skipped` array inside a 47 MB file -- the shape that let 7 paid-for companies be
   * skipped every night with nobody able to see it.
   */
  'tenant.source.external_company.list': {
    readonly companies: readonly ExternalCompanySummary[];
    readonly unmappedCount: number;
  };
  /**
   * WHICH ACCOUNTING SYSTEM KEEPS THIS COMPANY'S BOOKS, and the list to choose from.
   *
   * THERE IS NO CONNECTION STATE ON THIS SHAPE, and that is the requirement rather than an
   * omission: *"בלי מזהה ספק מומצא ובלי להציג ״מחובר״ לפני בדיקה"*. No status, no `connected`, no
   * freshness, no last-checked. A screen cannot claim a connection from this because there is
   * nothing here to claim it with.
   *
   * `selected: null` is *"טרם הוגדר"* -- never "none". `isSelectable: false` means the system it
   * names is retired or reclassified, REPORTED rather than hidden, because a stale selection that
   * rendered as unset would be a company nobody knows needs re-pointing.
   */
  'companies.source_system.read': {
    readonly selected: {
      readonly systemCode: string;
      readonly displayName: string;
      readonly isSelectable: boolean;
    } | null;
    readonly selectable: readonly { readonly systemCode: string; readonly displayName: string }[];
  };
  /** The saved selection is echoed back, so the screen shows what was STORED, not what was typed. */
  'companies.source_system.select': {
    readonly outcome: 'set' | 'unchanged';
    readonly selected?: {
      readonly systemCode: string;
      readonly displayName: string;
      readonly isSelectable: boolean;
    } | null;
  };
  /* The handler answers with the summary itself, not a wrapper — and a 404 when there is none. */
  'tenant.billing.read': SubscriptionSummary;
  'tenant.capacity.read': { readonly capacity: readonly CapacityUsage[] };
  'tenant.statement.read': BillingStatement;
  'tenant.billing.estimate.read': NextChargeEstimate;
  'tenant.plan.offers.read': PlanOffersView;
  /**
   * ITEM 4. The SERVER's own outcome, carried through so the screen says what happened.
   *
   * `changed` is what distinguishes the two, and `downgradeEffectiveFrom` appears on BOTH — on a
   * success it is the day the scheduled move will happen, and the refusing shape survives only
   * for a downgrade below current usage, which carries `limitsOverUsage` instead. A reader that
   * treated the presence of that field as a refusal would tell a customer their downgrade failed
   * when it is booked.
   */
  /**
   * ITEM 4. THE SUCCESS SHAPE ONLY. A refusal is a 409 and reaches the client as a
   * `ConflictError` through the error taxonomy — `error` carries the reason, and
   * `lifecycleText` turns it into words. Declaring the refusing shape here too would invite a
   * caller to read `result.reason` on an answer that never carries one.
   *
   * `changed` IS ALWAYS TRUE HERE, and `downgradeEffectiveFrom` is what distinguishes the two
   * successes: a plan moved now, or a move BOOKED for the day the period ends. 08 §5.3 —
   * returning one shape for both would make the screen say the plan changed about a plan nobody
   * is on yet.
   */
  'tenant.subscription.plan.change': {
    readonly changed: boolean;
    readonly downgradeEffectiveFrom?: string;
    readonly supersededSchedule?: boolean;
  };
  /**
   * `policy: null` MEANS UNREADABLE AND NEVER `optional`. Migration 0012 removed the column
   * DEFAULT so a policy nobody chose cannot be applied silently; a client that substituted
   * one would be that default in a different file.
   */
  'tenant.security.mfa_policy.read': { readonly policy: string | null };
  /* The settle press answers with the run's own report, for the office's own one line. */
  'tenant.change_request.submit': { readonly submitted: true; readonly changeRequestUid: string };
  'tenant.change_request.read': { readonly requests: readonly ChangeRequestRead[] };
  'tenant.invoice.read': { readonly invoices: readonly InvoiceLineRead[] };
  'tenant.invoice.detail': InvoiceLineRead;
  'tenant.subscription.cancel.preview': CancellationPreviewRead;
  'tenant.subscription.cancel': {
    readonly cancelled: true;
    readonly accessEndsOn: string;
    readonly commentTruncated: boolean;
  };
  'tenant.module_offer.read': ModuleOffersView;
  /**
   * THE WRITE ANSWERS WITH DATES AND NOT WITH A FLAG.
   *
   * `effectiveFrom` is the day the office holds it from — today — and `firstInvoicePeriodEndsOn`
   * is the period the prorated share appears on. A bare `{purchased: true}` would leave the
   * confirmation unable to say when anything happens, and *"קונה מיד"* is a claim about a date.
   *
   * A REFUSAL IS A 422 AND THROWS, so this shape is the success only. The named reasons are
   * `BadRequestError`'s territory — see the panel.
   */
  'tenant.module.purchase': {
    readonly purchased: true;
    readonly skuCode: string;
    readonly effectiveFrom: string;
    readonly firstInvoicePeriodEndsOn: string | null;
  };
  'platform.tenant.read': { readonly tenants: readonly TenantAdminSummary[] };
  'platform.support_grant.read': { readonly grants: readonly SupportGrantSummary[] };
  /**
   * ONE CUSTOMER'S SUBSCRIPTION, READ FROM THE PLATFORM SURFACE.
   *
   * `subscription` is `null` for a tenant nothing has been sold to yet -- an ordinary state
   * between provisioning and the first sale. An unknown tenant is a 404 and never reaches here,
   * so the two negatives stay two.
   */
  'platform.subscription.read': { readonly subscription: PlatformSubscriptionSummary | null };
  /** Commercial and platform-administrative events across every customer. */
  'platform.audit.read': { readonly events: readonly PlatformAuditEventSummary[] };
  /** Which front door this browser is at, as the SERVER sees it. Presentation only. */
  /*
   * THE PORTAL, AND WHICH ENVIRONMENT IT IS.
   *
   * `environment` feeds the indicator that replaces the approved prototype's demo-data ribbon.
   * It is OPTIONAL and nullable on purpose: optional so a server that predates this field is
   * `undefined` rather than a contract violation, nullable so a deployment that never declared
   * one is an explicit unidentified rather than a guess. The two cases read the same to the
   * interface and neither of them invents a value.
   */
  'portal.identity.read': {
    readonly portal: 'TENANT' | 'PLATFORM';
    readonly environment?: string | null;
    /**
     * WHICH RELEASE THE SERVER IS. Optional and nullable for the same reason as `environment`:
     * optional so a server predating the field is not a contract violation, nullable so one that
     * cannot state its identity says so. About System renders either as "unavailable" and never
     * as a version.
     */
    readonly release?: { readonly tag: string | null; readonly commit: string | null };
  };
  'platform.metrics.read': { readonly metrics: PlatformMetricsSummary };
  'platform.price_book.read': { readonly priceBooks: readonly PriceBookSummary[] };
  'platform.price_book.items.read': { readonly items: readonly PriceItemSummary[] };
  'platform.price_book.create': { readonly priceBookUid: string };
  'platform.price_book.item.add': { readonly ok: true };
  'platform.price_book.publish': { readonly ok: true };
  'platform.subscription.price_book.read': {
    readonly pinned: { readonly priceBookUid: string; readonly code: string } | null;
  };
  'platform.discount.read': { readonly benefits: readonly BenefitView[] };
  'platform.subscription.price_book.set': { readonly ok: true; readonly priceBookUid: string | null };
  'platform.catalogue.plan.read': { readonly plans: readonly PlanCatalogueEntry[] };
  'platform.catalogue.sku.read': { readonly skus: readonly SkuCatalogueEntry[] };
  /* X3. OBJECT ENVELOPES, never bare arrays (MC-2), and each write answers with the stable uid. */
  'platform.catalogue.reference.read': {
    readonly modules: readonly ModuleCatalogueEntry[];
    readonly chargeDimensions: readonly ChargeDimensionEntry[];
  };
  /*
   * THE UID IS THE CODE, and that is not a shortcut. `billing.plan` and `billing.product_sku` have a
   * UNIQUE code and no uid column; adding one would be a migration, and this wave's migration count
   * is ruled. So the stable identifier the Master gave these rows travels, honestly named.
   */
  'platform.catalogue.plan.create': { readonly planCode: string };
  'platform.catalogue.sku.create': { readonly skuCode: string };
  'platform.catalogue.sku.module.attach': { readonly skuCode: string };
  'platform.catalogue.plan.sku.include': { readonly planCode: string };
  'platform.catalogue.plan.sku.quantity': { readonly planCode: string; readonly skuCode: string };
  /** `liveHoldersRetained` is what was NOT revoked -- the removal withdraws nothing. */
  'platform.catalogue.plan.sku.exclude': {
    readonly planCode: string;
    readonly skuCode: string;
    readonly liveHoldersRetained: number;
  };
  'platform.incidents.read': { readonly health: PlatformHealthSummary };
  'platform.release.available.read': ReleaseOverview;
  'platform.release.update.start': { readonly deploymentUid: string };
  'platform.release.accept': { readonly acceptanceUid: string };
  'platform.capacity.read': { readonly capacity: readonly CapacityUsage[] };
  'platform.tenant.statement.read': BillingStatement;
  'platform.tenant.estimate.read': NextChargeEstimate;
  'platform.billing.due.read': RunReport;
  'platform.billing.run.execute': RunReport;
  'platform.billing.credit.remaining': { readonly remaining: string; readonly currencyCode: string };
  /*
   * `moneyMoved` IS ALWAYS `false` AND IS A FIELD. *"אין לראות בזיכוי הוכחת תשלום."*
   * A missing field would have to be remembered; a `false` is read.
   */
  'platform.billing.credit.issue': {
    readonly issued: true;
    readonly documentUid: string;
    readonly remainingAfter: string;
    readonly moneyMoved: false;
  };
  'platform.change_request.read': { readonly requests: readonly ChangeRequestRead[] };
  'platform.change_request.resolve': { readonly resolved: true };
  'platform.dunning.read': ArrearsReport;
  'platform.dunning.review': DunningReviewRead;
  'platform.dunning.policy.read': DunningPolicyReport;
  'platform.dunning.policy.set': { readonly recorded: true };
  'platform.policy.read': { readonly policy: CommercialPolicySummary };
  'platform.policy.trial_days.set': { readonly ok: true; readonly value: string };
  /**
   * §10ב. `declared: false` carries `open: false` IN THE SHAPE, mirroring `SignupDoorView` on the
   * server: an estate with no policy row has a SHUT door, not an unknown one, and a client that
   * could express "undeclared and therefore unknown" would render uncertainty for a definite state.
   */
  'platform.signup_door.read': {
    readonly door:
      | {
          readonly declared: true;
          readonly open: boolean;
          readonly rawValue: string;
          readonly updatedAtUtc: string;
          readonly reason: string;
        }
      | { readonly declared: false; readonly open: false };
  };
  'platform.signup_door.set': { readonly ok: true; readonly value: string };
  'platform.catalogue.plan.public_signup.set': { readonly ok: true; readonly planCode: string };
  'tenant.audit.read': { readonly events: readonly AuditEventSummary[] };
  'platform.tenant.offboarding.read': OffboardingStateView;
  /**
   * The SAME `PurgeEligibility` the view above carries, from its own route.
   *
   * Two routes answering with one shape is not duplication here: the state read is a snapshot of the
   * card, and the eligibility read is the question *"may the governed deletion workflow begin right
   * now"* — asked at the moment somebody is about to act, against a retention clock that runs. A
   * card rendered ten minutes ago is not an answer to that.
   */
  'platform.tenant.offboarding.purge_eligibility': PurgeEligibility;
  'user.session.read': { readonly sessions: readonly UserSessionSummary[] };
  'tenant.user.session.read': { readonly sessions: readonly UserSessionSummary[] };
  'user.profile.read': {
    readonly displayName: string;
    readonly email: string;
    readonly phone: string | null;
    /** Whether this account signs in WITH its address. The server decides; the screen repeats it. */
    readonly loginFollowsEmail: boolean;
    readonly pendingEmailChange: {
      readonly newEmail: string;
      readonly requestedAtUtc: string;
      readonly expiresAtUtc: string;
    } | null;
  };
  'auth.email_change.confirm': { readonly confirmed: boolean; readonly loginChanged: boolean };
  'user.profile.update': { readonly updated: boolean };
  'user.email.change_request': { readonly requested: boolean; readonly expiresAtUtc: string };
  /*
   * EVERY PREFERENCE THIS ACCOUNT HAS SAVED, and a key is ABSENT rather than null when there is
   * nothing stored for it -- which is the distinction the home screen needs: *nothing saved* means
   * keep what this device has and push it up, and that is not the same as *saved as empty*.
   *
   * `value` is `unknown` because the server returns what it parsed and the shape is per key. The
   * caller narrows; `preferences.ts` is the one place that does.
   */
  'user.preference.read': {
    readonly preferences: readonly {
      readonly key: PreferenceKey;
      readonly value: unknown;
      readonly updatedAtUtc: string;
    }[];
  };
  'user.preference.update': { readonly saved: boolean };
  /*
   * EVERY DOMAIN IS IN THE ANSWER, INCLUDING THE ONES WITH NO SOURCE. A card absent because
   * nothing was configured would be indistinguishable from a card somebody removed, and the
   * Owner's rule is that an unavailable capability is SHOWN with its reason.
   */
  'user.market.read': { readonly cards: readonly MarketCardView[] };
  /*
   * ── AND THE READS. `mayAct` IS THE SERVER'S ANSWER AND THE SCREEN REPEATS IT. ─────────
   *
   * The screen does not work out "may I act on this" from a capability list. That rule is about a
   * ROW -- assigned to me, or raised by me -- and a second implementation of it in the browser
   * would differ from the server's on exactly the row nobody thought about: an item somebody
   * raised and then assigned away. So the server decides per row and the screen renders the flag.
   */
  'tenant.work_item.list': { readonly items: readonly WorkItemSummary[] };
  'tenant.work_item.read': WorkItemDetailView;
  'tenant.work_item.create': { readonly uid: string };
  'tenant.reminder_rule.list': { readonly rules: readonly ReminderRuleView[] };
  'tenant.reminder_rule.put': { readonly uid: string };
  'companies.reminder_setting.list': {
    readonly settings: readonly CompanyReminderSettingView[];
  };
  'companies.reminder_setting.put': { readonly uid: string };
  'public.signup.plans': { readonly plans: readonly PublicPlanOfferView[] };
  'public.signup.quote': SignupQuoteView;
  'public.signup.start': { readonly accepted: true };
  'public.signup.verify': {
    readonly signupUid: string;
    readonly legalName: string;
    readonly email: string;
  };
  'public.signup.payment_method': { readonly verified: true; readonly provider: string };
  'public.payment.page': { readonly redirectUrl: string; readonly paymentPageUid: string };
  'public.payment.settle': {
    readonly verified: true;
    readonly provider: string;
    readonly cardLast4: string | null;
    readonly replacedPrevious: boolean;
  };
  'tenant.payment_method.page': {
    readonly redirectUrl: string;
    readonly paymentPageUid: string;
  };
  /**
   * `card` IS NULL FOR AN OFFICE WITH NO CARD ON FILE, and that is a 200 rather than a 404: the
   * screen needs the absence in order to offer the button that fixes it.
   *
   * NO TOKEN IS IN THIS SHAPE. The store's reader does not select the column, so there is nothing
   * to leave out here — the absence is structural rather than a field somebody remembered to
   * omit.
   */
  'tenant.payment_method.read': { readonly card: StoredCardView | null };
  'tenant.payment_method.charges': { readonly charges: readonly ChargeAttemptView[] };
  'platform.clearing.read': ClearingOverviewView;
  /** The provider's address for the original document. Fetched when a person asks; never cached. */
  'tenant.invoice.download': { readonly url: string };
  'public.signup.complete': {
    readonly completed: true;
    readonly tenantUid: string;
    readonly expiresAt: string | null;
  };
  'companies.work_item.create': { readonly uid: string };
  'tenant.work_item.state': { readonly moved: boolean };
  'tenant.work_item.assign': { readonly assigned: boolean };
  'tenant.work_item.comment': { readonly added: boolean };
  'tenant.work_item.read_mark': { readonly read: boolean };
  'tenant.user.update': { readonly updated: boolean };
  'user.session.revoke_all': { readonly revoked: boolean; readonly count: number };
  'security.support_grant.read': { readonly grants: readonly TenantSupportGrantView[] };
  'security.support_grant.consent': { readonly consented: boolean; readonly grantUid: string };
  'security.support_grant.revoke': { readonly revoked: boolean; readonly grantUid: string };
  'platform.tenant.create': { readonly tenantUid: string };
  /*
   * ── WHAT THE SERVER ACTUALLY ANSWERS, AND THIS DECLARED A QUARTER OF IT ────────────────────
   *
   * **Owner, 2026-10-23 §8:** *"ההזמנה נמסרת כקובץ ולא בדואר, והממשק חייב לומר זאת בפשטות במקום
   * לרמוז שהגיע מייל."*
   *
   * `routes/provisioning.ts` answers **202** with `delivery` whenever the invitation was written
   * and the message was not sent — the whole point of the status — and this type named only
   * `invitationUid`. So the one fact §8 is about reached the browser on every call and was
   * unreachable in code: the wizard could only say "נשלחה הזמנה", which is what it said.
   *
   * `delivery` is `string` rather than `MailDeliveryReason` BECAUSE THE ROUTE SENDS A THIRD VALUE:
   * `tenant_not_activated`, when the mail went out and the tenant did not activate. Narrowing it
   * here to the two mail outcomes would make a real third answer unrepresentable, and the screen
   * would render it as one of the two it is not.
   */
  'platform.tenant.activate': {
    readonly invitationUid: string;
    readonly expiresAt?: string | null;
    /** `false` with a live invitation is a REPORTED partial outcome, not a failure. */
    readonly activated?: boolean;
    /** Absent ⇒ 201: the message was accepted. `mail_suppressed` ⇒ nobody was sent anything. */
    readonly delivery?: string;
    /**
     * WHERE THE MESSAGE WENT — `file` or `none`. **Owner, 2026-10-23 §8.**
     *
     * The decisive field, and the reason `delivery` alone was not enough: on this estate the mail
     * port is a DIRECTORY, so it reports `sent`, the route answers 201 with no `delivery`, and a
     * console reading only that says "ההזמנה נשלחה" about a message nobody posted. `file` is what
     * makes the screen able to say the invitation is waiting in an artifact.
     *
     * `string` and not a two-member union: a sending adapter will add its own value, and a client
     * that refused to parse a response because the estate grew one would be the stricter-looking
     * and worse behaviour.
     */
    readonly deliveryChannel?: string;
    readonly commercialReason?: string;
    readonly supersededInvitationUid?: string;
  };
  /*
   * THE UID IS PART OF THE ANSWER AND WAS NOT PART OF THE TYPE. The route returns
   * `{ applied, outcome, discountUid, dualControlUnavailable }` and this declared one field, so
   * the one thing the second approver needs -- the id of the decision that is waiting -- was
   * invisible to the browser. There is no reader for a pending decision anywhere in this
   * product, which made the id unrecoverable once the screen had discarded it.
   */
  'platform.discount.apply': {
    readonly outcome?: string;
    readonly discountUid?: string;
  };
  'platform.discount.apply_full': {
    readonly outcome?: string;
    readonly discountUid?: string;
  };
  /**
   * 08 §5.3. `assigned` is APPLIED and nothing else: a downgrade asked for mid-period is RECORDED
   * for the day after the period ends, and then `assigned` is `false` and `scheduledFrom` is that
   * day. A screen that read only `assigned` would say nothing happened; one that read neither
   * would say the plan was assigned. Both are wrong, which is why the field is here.
   */
  'platform.subscription.plan.assign': {
    readonly overCapacityBy?: number;
    readonly assigned?: boolean;
    readonly scheduledFrom?: string;
    readonly supersededSchedule?: boolean;
  };
  'platform.subscription.sku.add': { readonly overCapacityBy?: number };
  'platform.subscription.plan.apply': {
    /** Moves only when a line was written (08 §8.3); `null` when the route reported none. */
    readonly entitlementVersion: number | null;
    readonly included: readonly string[];
    readonly alreadyLive: readonly string[];
    readonly notActive: readonly { readonly skuCode: string; readonly status: string }[];
  };
  'platform.subscription.state.change': { readonly overCapacityBy?: number };
  'platform.trial.set': { readonly overCapacityBy?: number };
  'platform.trial.extend': { readonly overCapacityBy?: number };
  'platform.limit.company.set': { readonly overCapacityBy?: number };
  'platform.limit.seat.set': { readonly overCapacityBy?: number };
  'platform.tenant.offboarding.reinstate': { readonly mustReestablish?: unknown };
  'platform.tenant.offboarding.terminate': { readonly revoked?: unknown };
  'companies.company.create': { readonly companyUid: string };
  'tenant.grant.create': { readonly granted: number };
  'tenant.role.create': { readonly ok?: boolean };
  'tenant.user.role.assign': { readonly ok: boolean; readonly roleCount: number };
  'tenant.user.invite': { readonly invitationUid: string; readonly delivery?: unknown };
  'tenant.invitation.role.record': { readonly ok: boolean; readonly recorded: number };
  'tenant.invitation.access.record': { readonly ok: boolean; readonly recorded: number };
  /*
   * NAMES, and never ids. The screen reports *"ממתין להפעלה לאחר הצטרפות"* beside the role and the
   * company a person will arrive with, and a uid in front of an administrator is a number.
   * `companyName` is nullable because a company deleted after the choice was made does not erase
   * the fact that the choice exists -- the redemption records exactly that refusal.
   */
  'tenant.invitation.intent.read': {
    readonly pending: readonly {
      readonly kind: string;
      readonly roleCode: string | null;
      readonly companyUid: string | null;
      readonly companyName: string | null;
      readonly accessLevel: string | null;
    }[];
  };
  /*
   * EVERY INVITATION THIS OFFICE HAS OUT. **2026-10-28.**
   *
   * `status` carries `issued | revoked | expired` and the screen writes each one out: an address
   * that cannot be re-invited and a row that has silently vanished look the same from outside and
   * have different remedies. ACCEPTED ones are not here — they are members, and the members list
   * answers for them; the same person in two lists under two identities is how a count stops
   * adding up.
   *
   * NO TOKEN, no hash and no credential: the uid, the address, two dates and what is waiting.
   */
  'tenant.invitation.read': {
    readonly invitations: readonly {
      readonly invitationUid: string;
      readonly email: string;
      readonly displayName: string;
      readonly status: string;
      readonly invitedAtUtc: string;
      readonly expiresAtUtc: string;
      readonly intents: readonly {
        readonly kind: string;
        readonly roleCode: string | null;
        readonly roleNameHe: string | null;
        readonly companyUid: string | null;
        readonly companyName: string | null;
        readonly accessLevel: string | null;
      }[];
    }[];
  };
  'tenant.user.grant_outcome.read': {
    readonly outcomes: readonly {
      readonly kind: string;
      readonly roleCode: string | null;
      readonly roleNameHe: string | null;
      readonly companyUid: string | null;
      readonly companyName: string | null;
      readonly accessLevel: string | null;
      /** The redemption's own classification. `grantOutcomeText` turns it into office Hebrew. */
      readonly outcome: string;
      readonly decidedAtUtc: string;
    }[];
  };
  'tenant.user.invitation.resend': { readonly delivery?: unknown };
  'tenant.user.session.revoke': { readonly revoked?: number };
  'tenant.source.probe': {
    readonly ok: boolean;
    readonly failure?: { readonly kind?: string };
    readonly checkedAt?: string;
  };
}

/**
 * The type an undeclared write body resolves to. Nothing inhabits it, so the call does not compile
 * and the error names the fix. This is the mechanism that makes "wire a new action" a decision:
 * a screen cannot start posting to a route without stating what it posts.
 */
export interface UndeclaredBody {
  readonly __declareThisOperationInOperationBodies: never;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// THE TYPE-LEVEL PATH CONTRACT
// ══════════════════════════════════════════════════════════════════════════════════════════════════

/** `'/api/x/{a}/y/{b}'` → `'a' | 'b'`. The URL's own parameters, read by the compiler. */
type PathParam<S extends string> = S extends `${string}{${infer P}}${infer Rest}`
  ? P | PathParam<Rest>
  : never;

export type ParamsOf<Id extends OperationId> = PathParam<(typeof OPERATIONS)[Id]['path']>;

type MethodOf<Id extends OperationId> = (typeof OPERATIONS)[Id]['method'];

type BodyOf<Id extends OperationId> = Id extends keyof OperationBodies
  ? OperationBodies[Id]
  : UndeclaredBody;

export type ResultOf<Id extends OperationId> = Id extends keyof OperationResults
  ? OperationResults[Id]
  : void;

type ParamsField<Id extends OperationId> = [ParamsOf<Id>] extends [never]
  ? { readonly params?: never }
  : { readonly params: Readonly<Record<ParamsOf<Id>, string>> };

type BodyField<Id extends OperationId> = MethodOf<Id> extends 'GET'
  ? { readonly body?: never }
  : { readonly body: BodyOf<Id> };

export type CallOptions<Id extends OperationId> = ParamsField<Id> &
  BodyField<Id> & { readonly signal?: AbortSignal };

/**
 * A compile-time assertion that `OperationBodies` and `OperationResults` name only declared
 * operations. Without it a typo in a key would silently create a shape no call site can reach — the
 * "written, correct, reaches nothing" defect, at the type level.
 */
type UnknownKeys = Exclude<keyof OperationBodies | keyof OperationResults, OperationId>;
const _everyDeclaredShapeNamesARealOperation: [UnknownKeys] extends [never] ? true : UnknownKeys =
  true;
void _everyDeclaredShapeNamesARealOperation;

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// THE CALL
// ══════════════════════════════════════════════════════════════════════════════════════════════════

/**
 * Substitutes the declared parameters into the declared template.
 *
 * Every value is percent-encoded, and the result is asserted to carry no leftover placeholder. A
 * remaining `{` would mean the type-level extraction and the runtime substitution disagree, which is
 * a programming error rather than a user-visible failure, so it throws where a developer sees it.
 *
 * Exported for `reachability.test.ts`, which builds every operation's URL from sample parameters and
 * compares it against the baseline path — so the substitution itself is covered rather than assumed.
 */
export function buildOperationPath(
  spec: OperationSpec,
  params: Readonly<Record<string, string>> | undefined,
): string {
  const filled = spec.path.replace(/\{([a-z_0-9]+)\}/gu, (_match, name: string) => {
    const value = params?.[name];
    if (value === undefined || value === '') {
      throw new Error(
        `operation path ${spec.path} needs a value for {${name}} — an address built from an absent ` +
          'identifier would be a request against whatever the server resolves the empty segment to',
      );
    }
    return encodeURIComponent(value);
  });
  if (filled.includes('{') || filled.includes('}')) {
    throw new Error(`operation path ${spec.path} still holds a placeholder after substitution`);
  }
  return filled;
}

/**
 * THE ONLY WAY THIS APP TALKS TO THE SERVER.
 *
 * `client.ts` still owns the transport, the error taxonomy and the retry policy — a write is never
 * retried, and that rule stays where the reasoning for it is written. This adds the layer above it:
 * WHICH endpoint, with WHICH parameters, in WHICH shape, all decided by the table above rather than
 * by a template literal in a screen.
 *
 * `web-client-choke-point` (G-03) fails the build if any module under `apps/web/src` other than
 * `api/client.ts` and this file imports the transport directly, and if any call below is passed an
 * operation id the static scan cannot resolve to a literal. Those two rules are what let
 * `reachability.test.ts` say "these routes are reached and these are not" and be believed.
 */
type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

/**
 * Injectable for tests, and INERT OUTSIDE THEM.
 *
 * `globalThis.fetch` below is the only reference to the network in the whole app — the property
 * `web-client-choke-point` now enforces. This seam is the one way that property could be undone
 * from inside: a screen importing it could install a `fetch` of its own and issue anything.
 *
 * So it refuses outside a test build. That is a structural answer rather than another import
 * prohibition, because CM-013 is the finding that import prohibitions get spelled around — and a
 * seam that is dead in the shipped bundle cannot be spelled around at all.
 */
let fetchImpl: FetchLike | null = null;
export function __setFetchForTests(f: FetchLike | null): void {
  if (import.meta.env.MODE !== 'test') {
    throw new Error(
      'the transport seam is a TEST seam. A module that could replace fetch could issue any ' +
        'request it liked, which is exactly what the operation registry exists to prevent.',
    );
  }
  fetchImpl = f;
}

/**
 * HOW MANY TIMES A REQUEST MAY BE ATTEMPTED — **DERIVED FROM THE DECLARATION, NEVER PASSED IN.**
 *
 * This used to be a `retries` option on `request()`, with a runtime throw if a caller passed it
 * alongside a write. That check was correct and it was also an admission: the shape allowed the
 * dangerous call to be WRITTEN, so it needed guarding. Here there is no argument through which a
 * caller could ask for a write to be retried, so "a write is never retried" is a property of the
 * type rather than a rule somebody enforces.
 *
 * The reason has not changed: a client that resends a POST after the connection died cannot know
 * whether the server committed the first one. A GET may be resent because asking twice is asking.
 */
function attemptsFor(method: HttpMethod): number {
  return method === 'GET' ? 2 : 1;
}

/**
 * ONE BILLING DOCUMENT, as §11's tab reads it.
 *
 * `amountTotalDisplay` IS THE SERVER'S STRING — `117.00 ₪`. A client that re-formatted it would be
 * a second formatter, and the Owner's complaint was about exactly that: *"לא `390.0000 ILS`."* The
 * stored figures travel beside it for anything that needs a number, so nothing has to parse the
 * display string back.
 *
 * ALL OF `documentNumber`, `periodStart`, `periodEnd` AND `amountTotalDisplay` MAY BE NULL. 0036's
 * `pending_issue` and `provider_failed` are real states: *"אם מסמך טרם הופק או שספק המסמכים
 * נכשל, הצג את המצב האמיתי."*
 */
export interface InvoiceLineRead {
  readonly documentUid: string;
  readonly documentKind: string;
  readonly documentNumber: string | null;
  readonly periodStart: string | null;
  readonly periodEnd: string | null;
  readonly amountTotalDisplay: string | null;
  readonly amountExVat: string;
  readonly vatRatePct: string;
  readonly vatAmount: string;
  readonly amountTotal: string;
  readonly currencyCode: string;
  readonly status: string;
  readonly issuedAt: string | null;
  readonly providerFailureReason: string | null;
  readonly chargedOn: string;
  readonly downloadable: boolean;
}

/** What the cancellation tab must show BEFORE anything is typed. §12. */
export interface CancellationPreviewRead {
  readonly subscriptionUid: string;
  readonly state: string;
  /** `YYYY-MM-DD`. The last day of access — the paid period's end, or the trial's. */
  readonly accessEndsOn: string;
  readonly furtherChargeExpected: boolean;
  readonly alreadyCancelled: boolean;
  readonly confirmationWord: string;
  readonly reasons: readonly string[];
}

export function callOperation<Id extends OperationId>(
  id: Id,
  options: CallOptions<Id>,
): Promise<ResultOf<Id>> {
  const spec: OperationSpec = OPERATIONS[id];
  return issue<ResultOf<Id>>(spec, buildOperationPath(spec, options.params), options);
}

/**
 * THE ONE PLACE THIS APP TOUCHES THE NETWORK. **CM-013.**
 *
 * It is `function`-private to this module: not exported, so there is nothing for another module to
 * import under any spelling. That is the correction. The rule used to be *"only these two files may
 * import `request`/`get`/`post`"*, enforced by scanning import statements — and three mutations beat
 * it with CI green (a namespace import, a `./client.js` specifier, a re-export through a third
 * module), while a screen calling `fetch` directly was never covered by the rule at all.
 *
 * `path` is built by `buildOperationPath` from the approved baseline template, so no free-form
 * address reaches here even from inside this file.
 */
async function issue<T>(
  spec: OperationSpec,
  fullPath: string,
  options: { readonly body?: unknown; readonly signal?: AbortSignal },
): Promise<T> {
  // The table holds the DECLARED path, which is `/api`-prefixed; the fetch below re-applies it, so
  // the prefix is removed once here rather than being absent from the baseline.
  const path = fullPath.slice(API_BASE.length);
  const init = requestInit({
    method: spec.method,
    ...(options.body === undefined ? {} : { body: options.body }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  const attempts = attemptsFor(spec.method);
  let lastNetworkFailure: NetworkError | null = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    let res: Response;
    try {
      res = await (fetchImpl ?? globalThis.fetch)(`${API_BASE}${path}`, init);
    } catch {
      lastNetworkFailure = new NetworkError();
      // Only reachable for a GET; every other method left the loop at attempts === 1.
      continue;
    }
    return interpretResponse<T>(res);
  }

  throw lastNetworkFailure ?? new NetworkError();
}
